import {Component, OnInit} from "@angular/core";
import {User} from "../../../shared/models/user";
import {UserService} from "../../../shared/services/user/user.service";
import {take} from "rxjs/operators";
import {Exam} from "../../../shared/models/exam";
import {ExamService} from "../../../shared/services/exam/exam.service";
import {environment} from "../../../../environments/environment";
import {FormControl} from "@angular/forms";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {Router} from "@angular/router";
import {StudentProfileModalComponent} from "../student-profile-modal/student-profile-modal.component";
import {ExamResultsModalComponent} from "../../exam/exam-results-modal/exam-results-modal.component";
import {CertificationTest} from "src/app/shared/models/certificationTest";
import {CertificationMockTest} from "src/app/shared/models/certificationMockTest";
import {StudentAttendanceConfirmationModalComponent} from "../student-attendance-confirmation-modal/student-attendance-confirmation-modal.component";
import {StudentVoucherProfessionalModalComponent} from "../student-voucher-professional-modal/student-voucher-professional-modal.component";
import {SessionEditModalComponent} from "../../session/session-edit-modal/session-edit-modal.component";
import {MockTestConfirmationModalComponent} from "../mock-test-confirmation-modal/mock-test-confirmation-modal.component";


@Component({
    "selector": "app-student-profile",
    "templateUrl": "./student-profile.component.html",
    "styleUrls": ["./student-profile.component.scss"]
})
export class StudentProfileComponent implements OnInit {

    user: User;
    exams: Array<Exam>;
    baseUrl = environment.baseUrl;
    voucherCodeControl: FormControl;
    profileUpdated: boolean;
    profileUpdateError: boolean;
    registerError: boolean;
    loading: boolean;

    constructor (
        private userService: UserService,
        private examService: ExamService,
        private modalService: NgbModal,
        private router: Router
    ) {
        this.voucherCodeControl = new FormControl("");
        this.profileUpdated = false;
    }

    ngOnInit (): void {
        this.userService.getMe().pipe(take(1)).
            subscribe((user: User) => {
                this.user = user;
            });
        this.fetchExams();
    }

    onModelChange (wants_to_be_published: boolean) {
        this.profileUpdateError = false;
        this.profileUpdated = false;
        const submitUser = new User(this.user);
        submitUser.student_profile.wants_to_be_published = wants_to_be_published;


        this.userService.partialUpdate(
            this.user.id,
            {
                "student_profile": {
                    wants_to_be_published
                }
            }
        ).pipe(take(1)).
            subscribe(
                (res: User) => {
                    this.user = res;
                    this.profileUpdated = true;
                },
                (err: any) => {
                    this.profileUpdateError = true;
                    throw err;
                }
            );
    }

    fetchExams () {
        this.loading = true;
        this.examService.list().pipe(take(1)).
            subscribe((exams: Array<Exam>) => {
                this.exams = exams;
                this.loading = false;

                for (const exam of this.exams) {
                    if (exam.studentConfirmedAt == null && exam.session != null) {
                        this.confirmStudentAttendance(exam);
                    }
                }
            });
    }

    openUpdateProfilModal () {
        const modalRef = this.modalService.open(
            StudentProfileModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.student = this.user;
        modalRef.result.then((res: User) => {
            this.user = res;
        }).catch(() => {
        // Dummy catch tu prevent exception thrown on modal dismissal
        });
    }

    openExamResultsModal (exam: Exam) {
        const modalRef = this.modalService.open(
            ExamResultsModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;
    }

    testStartedAt (test: CertificationTest) {
        for (const exam of this.exams) {
            for (const examResultValue of exam.examResultValues) {
                if (examResultValue.test.id == test.id) {
                    return examResultValue.startDatetime;
                }
            }
        }

        return null;
    }

    testHasBeenPassed (test: CertificationTest) {
        this.exams.forEach((exam) => {
            exam.examResultValues.forEach((examResultValue) => {
                if (examResultValue.test.id == test.id && examResultValue.is_done) {
                    return true;
                }
            });
        });

        return false;
    }

    testDone (exam: Exam, test: CertificationTest) {
        if (exam.success != null) {
            return true;
        }

        exam.examResultValues.forEach((examResultValue) => {
            if (examResultValue.test.id == test.id && examResultValue.is_done) {
                return true;
            }
        });

        return false;
    }

    confirmStudentAttendance (exam: Exam) {
        // Step 1: the candidate validates (and persists) their professional
        // situation for this passage before confirming presence.
        const modalRef = this.modalService.open(
            StudentVoucherProfessionalModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.exam = exam;
        modalRef.componentInstance.prefill = this.buildProfessionalPrefill(exam);
        modalRef.componentInstance.intro =
            "Vérifiez votre situation professionnelle pour ce passage avant de confirmer votre présence.";

        modalRef.result.then(() => {
            // Once validated, confirm presence (the professional fields now live
            // in this preceding step, not in the presence modal).
            const attendanceRef = this.modalService.open(
                StudentAttendanceConfirmationModalComponent,
                {size: "lg"}
            );
            attendanceRef.componentInstance.exam = exam;
            attendanceRef.result.then(() => {
                this.fetchExams();
            }).catch(() => {
            // Dummy catch to prevent exception thrown on modal dismissal
            });
        }).catch(() => {
        // Dummy catch to prevent exception thrown on modal dismissal
        });
    }

    // The "Modifier" button on a voucher: free edit of the professional situation.
    editProfessionalSituation (exam: Exam) {
        const modalRef = this.modalService.open(
            StudentVoucherProfessionalModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.exam = exam;
        modalRef.componentInstance.prefill = this.buildProfessionalPrefill(exam);
        modalRef.result.then(() => {
            this.fetchExams();
        }).catch(() => {
        // Dummy catch to prevent exception thrown on modal dismissal
        });
    }

    // Pre-fill the professional situation with this voucher's own values if set,
    // otherwise with the candidate's most recent filled voucher (this.exams is
    // ordered by most recent first).
    private buildProfessionalPrefill (exam: Exam) {
        const source = (exam.qualification_level || exam.profession || exam.objective)
            ? exam
            : this.exams.find(
                (other) => other.id !== exam.id &&
                    (other.qualification_level || other.profession || other.objective)
            );

        return {
            "qualification_level": source ? source.qualification_level : "",
            "profession": source ? source.profession : "",
            "objective": source ? source.objective : "",
        };
    }

    openMockTestConfirmation (exam: Exam, mockTest: CertificationMockTest) {
        const modalRef = this.modalService.open(
            MockTestConfirmationModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.exam = exam;
        modalRef.componentInstance.mockTest = mockTest;
        modalRef.result.then(() => {
            this.router.navigate(["/dashboard", "exam_mock_test", exam.id, mockTest.id]);
        }).catch(() => {
            // Dummy catch to prevent exception thrown on modal dismissal
        });
    }

    openSessionModal (session) {
        const modalRef = this.modalService.open(
            SessionEditModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.examSession = session;
        modalRef.result.then(() => {

        }).catch(() => {
        // Dummy catch tu prevent exception thrown on modal dismissal
        });

    }

}
